import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  Button, DatePicker, Form, Input, Radio, Select, TreeSelect, Typography, Upload, message
} from 'antd';
import { DeleteOutlined, EyeOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { useHrOptions } from '../../hooks/useHrOptions';
import { buildBudgetOrgTreeSelectProps } from '../../utils/orgTree';
import { validateChineseIdCard } from '../../utils/expensePayeeFieldValidate';
import AttachmentPreviewModal from '../AttachmentPreviewModal';
import './ExpenseClaimForm.css';

const { Text } = Typography;
const UPLOAD_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.bmp';

function FormSection({ title, children }) {
  return (
    <section className="expense-claim-section">
      <div className="expense-claim-section-title">{title}</div>
      {children}
    </section>
  );
}

function normalizeEnrollmentMonth(period) {
  if (!period) return undefined;
  if (dayjs.isDayjs(period)) return period.format('YYYY-MM');
  return String(period).slice(0, 7);
}

function createEmptyLine(defaultMonth) {
  return {
    employee_name: undefined,
    id_card: undefined,
    change_type: undefined,
    enrollment_month: defaultMonth,
    cost_org_code: undefined,
    social_insurance_company_id: undefined,
    insurance_purchase_type: undefined,
    apply_reason: undefined
  };
}

export function getSocialInsuranceChangeInitialValues(defaultMonth = dayjs().format('YYYY-MM')) {
  return {
    personnel_lines: [createEmptyLine(defaultMonth)],
    attachments: []
  };
}

export async function buildSocialInsuranceChangePayload(form, submit) {
  const values = submit ? await form.validateFields() : form.getFieldsValue(true);
  const personnel_lines = (values.personnel_lines || []).map((line) => ({
    employee_name: line.employee_name,
    id_card: line.id_card,
    change_type: line.change_type,
    enrollment_month: normalizeEnrollmentMonth(line.enrollment_month),
    cost_org_code: line.cost_org_code,
    social_insurance_company_id: line.social_insurance_company_id,
    insurance_purchase_type: line.insurance_purchase_type,
    apply_reason: line.apply_reason
  }));

  if (submit) {
    if (!personnel_lines.length) throw new Error('请至少添加一行人员明细');
    personnel_lines.forEach((line, index) => {
      const row = index + 1;
      if (!String(line.employee_name || '').trim()) throw new Error(`第 ${row} 行请填写姓名`);
      const idResult = validateChineseIdCard(line.id_card);
      if (!idResult.ok) throw new Error(`第 ${row} 行${idResult.message}`);
      if (!line.change_type) throw new Error(`第 ${row} 行请选择购买/减少类型`);
      if (!line.enrollment_month) throw new Error(`第 ${row} 行请选择参保时间`);
      if (!line.cost_org_code) throw new Error(`第 ${row} 行请选择费用成本归属中心`);
      if (!line.social_insurance_company_id) throw new Error(`第 ${row} 行请选择社保购买公司`);
      if (!line.insurance_purchase_type) throw new Error(`第 ${row} 行请选择社保购买类型`);
      if (!String(line.apply_reason || '').trim()) throw new Error(`第 ${row} 行请填写申请原因`);
    });
  }

  return {
    submit,
    personnel_lines,
    attachments: values.attachments || []
  };
}

function PersonnelLineRow({
  field,
  index,
  form,
  changeTypeOptions,
  insurancePurchaseTypeOptions,
  orgTreeProps,
  loadingMeta,
  defaultMonth,
  onRemove,
  canRemove
}) {
  const costOrgCode = Form.useWatch(['personnel_lines', field.name, 'cost_org_code'], form);

  const handleCostOrgChange = (orgCode) => {
    const lines = [...(form.getFieldValue('personnel_lines') || [])];
    const line = lines[index];
    if (!line) return;
    lines[index] = {
      ...line,
      cost_org_code: orgCode || undefined,
      social_insurance_company_id: undefined
    };
    form.setFieldsValue({ personnel_lines: lines });
  };

  return (
    <div className="social-insurance-line-row">
      <Form.Item
        {...field}
        className="social-insurance-line-field social-insurance-line-field--name"
        name={[field.name, 'employee_name']}
        label="姓名"
        rules={[{ required: true, message: '请填写姓名' }]}
      >
        <Input size="small" placeholder="姓名" maxLength={50} />
      </Form.Item>
      <Form.Item
        {...field}
        className="social-insurance-line-field social-insurance-line-field--id"
        name={[field.name, 'id_card']}
        label="身份证号"
        rules={[
          { required: true, message: '请填写身份证号' },
          {
            validator: (_, value) => {
              const result = validateChineseIdCard(value);
              return result.ok ? Promise.resolve() : Promise.reject(new Error(result.message));
            }
          }
        ]}
      >
        <Input size="small" placeholder="18位身份证号" maxLength={18} />
      </Form.Item>
      <Form.Item
        {...field}
        className="social-insurance-line-field social-insurance-line-field--type"
        name={[field.name, 'change_type']}
        label="购买/减少"
        rules={[{ required: true, message: '请选择' }]}
      >
        <Select size="small" placeholder="请选择" options={changeTypeOptions} loading={loadingMeta} />
      </Form.Item>
      <Form.Item
        {...field}
        className="social-insurance-line-field social-insurance-line-field--month"
        name={[field.name, 'enrollment_month']}
        label="参保时间"
        rules={[{ required: true, message: '请选择参保时间' }]}
        getValueProps={(value) => ({
          value: value ? dayjs(`${normalizeEnrollmentMonth(value)}-01`) : undefined
        })}
        normalize={(value) => (value ? value.format('YYYY-MM') : undefined)}
      >
        <DatePicker size="small" picker="month" format="YYYY-MM" />
      </Form.Item>
      <Form.Item
        {...field}
        className="social-insurance-line-field social-insurance-line-field--org"
        name={[field.name, 'cost_org_code']}
        label="费用成本归属中心"
        rules={[{ required: true, message: '请选择归属中心' }]}
      >
        <TreeSelect
          {...orgTreeProps}
          size="small"
          onChange={handleCostOrgChange}
        />
      </Form.Item>
      <Form.Item
        {...field}
        className="social-insurance-line-field social-insurance-line-field--company"
        name={[field.name, 'social_insurance_company_id']}
        label="社保购买公司"
        rules={[{ required: true, message: '请选择社保购买公司' }]}
      >
        <LineCompanySelect orgCode={costOrgCode} />
      </Form.Item>
      <Form.Item
        {...field}
        className="social-insurance-line-field social-insurance-line-field--ins-type"
        name={[field.name, 'insurance_purchase_type']}
        label="社保购买类型"
        rules={[{ required: true, message: '请选择类型' }]}
      >
        <Radio.Group
          size="small"
          options={insurancePurchaseTypeOptions}
          disabled={loadingMeta}
        />
      </Form.Item>
      <Form.Item
        {...field}
        className="social-insurance-line-field social-insurance-line-field--reason"
        name={[field.name, 'apply_reason']}
        label="申请原因"
        rules={[{ required: true, message: '请填写申请原因' }]}
      >
        <Input size="small" placeholder="申请原因" maxLength={200} />
      </Form.Item>
      {canRemove && (
        <div className="social-insurance-line-remove">
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        </div>
      )}
    </div>
  );
}

function LineCompanySelect({ orgCode, value, onChange, disabled }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectMode, setSelectMode] = useState('org_linked');

  useEffect(() => {
    if (!orgCode) {
      setOptions([]);
      setSelectMode('org_linked');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api.get('/hr/hr-social-insurance-change/social-insurance-companies', {
      params: { org_code: orgCode }
    })
      .then((res) => {
        if (cancelled || !res.data.success) return;
        const data = res.data.data || {};
        const companies = data.companies || [];
        setSelectMode(data.company_select_mode || 'org_linked');
        setOptions(companies.map((item) => ({ value: item.id, label: item.label || item.company_name })));
      })
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
          setSelectMode('org_linked');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [orgCode]);

  const placeholder = !orgCode
    ? '先选归属中心'
    : (selectMode === 'all' ? '请选择公司' : '请选择');

  return (
    <Select
      size="small"
      showSearch
      allowClear
      optionFilterProp="label"
      placeholder={placeholder}
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled || !orgCode}
      loading={loading}
    />
  );
}

export default function SocialInsuranceChangeForm({ form, initialValues, onValuesChange }) {
  const { allOrgs } = useHrOptions();
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [changeTypeOptions, setChangeTypeOptions] = useState([]);
  const [insurancePurchaseTypeOptions, setInsurancePurchaseTypeOptions] = useState([]);
  const [defaultMonth, setDefaultMonth] = useState(dayjs().format('YYYY-MM'));

  const attachments = Form.useWatch('attachments', form) || [];

  const orgTreeProps = useMemo(
    () => buildBudgetOrgTreeSelectProps(allOrgs, {
      placeholder: '请选择',
      style: { width: '100%' },
      includeSubProjects: true
    }),
    [allOrgs]
  );

  const loadMeta = useCallback(async () => {
    try {
      setLoadingMeta(true);
      const res = await api.get('/hr/hr-social-insurance-change/meta');
      if (!res.data.success) return;
      const data = res.data.data || {};
      setChangeTypeOptions(data.change_type_options || []);
      setInsurancePurchaseTypeOptions(data.insurance_purchase_type_options || []);
      if (data.default_enrollment_month) setDefaultMonth(data.default_enrollment_month);
    } catch (e) {
      message.error(e.response?.data?.message || '加载表单失败');
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  // 退回重提场景：先用已有 form_data 回填，再让下面的默认值逻辑在已有值时跳过
  useLayoutEffect(() => {
    if (!initialValues || !Object.keys(initialValues).length) return;
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  useLayoutEffect(() => {
    const lines = form.getFieldValue('personnel_lines');
    if (!lines?.length) {
      form.setFieldsValue(getSocialInsuranceChangeInitialValues(defaultMonth));
    }
  }, [defaultMonth, form]);

  const uploadAttachment = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post('/hr/hr-social-insurance-change/upload-attachment', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (!res.data.success) throw new Error(res.data.message || '上传失败');
    return res.data.data;
  };

  const handleAttachmentUpload = async (file) => {
    try {
      setUploadingAttachment(true);
      const data = await uploadAttachment(file);
      const uploaded = {
        file_name: data.file_name || file.name,
        file_path: data.file_path
      };
      form.setFieldsValue({
        attachments: [...(form.getFieldValue('attachments') || []), uploaded]
      });
      message.success('附件已上传');
    } catch (e) {
      message.error(e.response?.data?.message || e.message || '上传失败');
    } finally {
      setUploadingAttachment(false);
    }
    return Upload.LIST_IGNORE;
  };

  const handleAttachmentRemove = (index) => {
    form.setFieldsValue({
      attachments: attachments.filter((_, i) => i !== index)
    });
  };

  const openAttachmentPreview = (file) => {
    if (!file?.file_path) return;
    setPreviewItem({
      path: file.file_path,
      name: file.file_name || '附件'
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className="expense-claim-form social-insurance-change-form"
      onValuesChange={onValuesChange}
    >
      <FormSection title="人员明细">
        <Form.List name="personnel_lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <PersonnelLineRow
                  key={field.key}
                  field={field}
                  index={index}
                  form={form}
                  changeTypeOptions={changeTypeOptions}
                  insurancePurchaseTypeOptions={insurancePurchaseTypeOptions}
                  orgTreeProps={orgTreeProps}
                  loadingMeta={loadingMeta}
                  defaultMonth={defaultMonth}
                  canRemove={fields.length > 1}
                  onRemove={() => remove(field.name)}
                />
              ))}
              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                onClick={() => add(createEmptyLine(defaultMonth))}
              >
                添加人员
              </Button>
            </>
          )}
        </Form.List>
      </FormSection>

      <Form.Item className="social-fund-attachment-item" label="附件">
        <Upload
          accept={UPLOAD_ACCEPT}
          beforeUpload={handleAttachmentUpload}
          showUploadList={false}
          disabled={uploadingAttachment}
        >
          <Button size="small" icon={<UploadOutlined />} loading={uploadingAttachment}>上传附件</Button>
        </Upload>
        {attachments.length > 0 && (
          <div className="expense-line-attachment social-fund-attachment-list">
            {attachments.map((file, index) => (
              <div key={`${file.file_path}-${index}`} className="expense-line-attachment-file">
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  aria-label="预览附件"
                  className="expense-line-attachment-eye"
                  onClick={() => openAttachmentPreview(file)}
                />
                <Text ellipsis={{ tooltip: file.file_name || '附件' }} className="expense-line-attachment-name">
                  {file.file_name || '附件'}
                </Text>
                <Button
                  type="link"
                  size="small"
                  danger
                  className="expense-line-attachment-remove"
                  onClick={() => handleAttachmentRemove(index)}
                >
                  删除
                </Button>
              </div>
            ))}
          </div>
        )}
        <Form.Item name="attachments" hidden initialValue={[]}><Input hidden /></Form.Item>
      </Form.Item>

      <AttachmentPreviewModal
        item={previewItem}
        open={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
      />
    </Form>
  );
}
